import { ScrapEyesEventWithReviewData } from "components/manualReview/contexts/ManualReviewEventContext";
import { useHotkeyContext } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/HotkeyContext";
import { useVideoControl } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { Dayjs } from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { MANUAL_REVIEW_SCOPE } from "../hotkeyScopes";

type NavigationMode = "single" | "conflicts";

const NAVIGATION_SEEK_OFFSET_SECONDS = 0.5;

interface UseManualReviewVideoPlayerStateReturn {
  isModalOpen: boolean;
  selectedEvent: ScrapEyesEventWithReviewData | null;
  selectEvent: (eventId: string, mode: NavigationMode) => void;
  handleModalClose: () => void;
  navigatePrevious: () => void;
  navigateNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
}

export const useManualReviewVideoPlayerState = ({
  events,
  videoStart,
}: {
  events?: ScrapEyesEventWithReviewData[];
  videoStart: Dayjs;
}): UseManualReviewVideoPlayerStateReturn => {
  const {
    state: { currentTime },
    actions: { seekTo },
  } = useVideoControl();

  const { getHotkey } = useHotkeyContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [navigationMode, setNavigationMode] =
    useState<NavigationMode>("single");

  // Find the currently selected event
  const selectedEvent = useMemo(() => {
    if (!selectedEventId || !events) return null;
    return events.find((e) => e.id === selectedEventId) ?? null;
  }, [selectedEventId, events]);

  // Filter events for conflict navigation mode
  const conflictEvents = useMemo(() => {
    if (!events) return [];
    return events.filter(
      (event) =>
        "review_reasons" in event &&
        Array.isArray(event.review_reasons) &&
        event.review_reasons.length > 0
    );
  }, [events]);

  // Get navigable events based on mode
  const navigableEvents = useMemo(() => {
    if (navigationMode === "conflicts") {
      return conflictEvents;
    }
    return events ?? [];
  }, [navigationMode, conflictEvents, events]);

  // Find current index in navigable events
  const currentIndex = useMemo(() => {
    if (!selectedEventId) return -1;
    return navigableEvents.findIndex((event) => event.id === selectedEventId);
  }, [selectedEventId, navigableEvents]);

  // Navigation state
  const hasPrevious = currentIndex > 0;
  const hasNext =
    currentIndex >= 0 && currentIndex < navigableEvents.length - 1;

  const selectEvent = useCallback(
    (eventId: string, mode: NavigationMode) => {
      const event = events?.find((e) => e.id === eventId);
      if (event) {
        setSelectedEventId(eventId);
        setIsModalOpen(true);
        setNavigationMode(mode);
      }
    },
    [events]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setNavigationMode("single");
  }, []);

  const navigatePreviousEvent = useCallback(() => {
    if (hasPrevious && currentIndex > 0) {
      const prevEvent = navigableEvents[currentIndex - 1];
      if (prevEvent) {
        setSelectedEventId(prevEvent.id);
        const eventStartSeconds = prevEvent.start.diff(videoStart, "second");
        seekTo(eventStartSeconds + NAVIGATION_SEEK_OFFSET_SECONDS);
      }
    }
  }, [hasPrevious, currentIndex, navigableEvents, videoStart, seekTo]);

  const navigateNextEvent = useCallback(() => {
    if (hasNext && currentIndex < navigableEvents.length - 1) {
      const nextEvent = navigableEvents[currentIndex + 1];
      if (nextEvent) {
        setSelectedEventId(nextEvent.id);
        const eventStartSeconds = nextEvent.start.diff(videoStart, "second");
        seekTo(eventStartSeconds + NAVIGATION_SEEK_OFFSET_SECONDS);
      }
    }
  }, [hasNext, currentIndex, navigableEvents, videoStart, seekTo]);

  const editLastEvent = useCallback(() => {
    if (!events || events.length === 0) return;

    // Find the most recent event that starts before or at the current time
    const currentTimestamp = videoStart.add(currentTime, "second");
    const sortedEvents = [...events].sort(
      (a, b) => b.start.valueOf() - a.start.valueOf()
    );
    const lastEvent = sortedEvents.find(
      (event) => !event.start.isAfter(currentTimestamp)
    );

    if (lastEvent) {
      selectEvent(lastEvent.id, "single");
    }
  }, [events, currentTime, selectEvent, videoStart]);

  const editLastEventKey = getHotkey("editLastEvent");
  useHotkeys(
    editLastEventKey,
    editLastEvent,
    { enableOnFormTags: false, scopes: [MANUAL_REVIEW_SCOPE] },
    [editLastEventKey, editLastEvent]
  );

  return {
    isModalOpen,
    selectedEvent,
    selectEvent,
    handleModalClose,
    navigatePrevious: navigatePreviousEvent,
    navigateNext: navigateNextEvent,
    hasPrevious,
    hasNext,
  };
};
